import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ArrowUpRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import ListingCard from '../components/ListingCard';
import CornerOrnament from '../components/CornerOrnament';
import { getImageUrl } from '../lib/utils-img';
import SEO, { businessJsonLd, websiteJsonLd } from '../components/SEO';
import { useContent } from '../lib/content';
import Editable from '../components/Editable';
import EditableImage from '../components/EditableImage';
import API from '../lib/api';

export default function HomePage() {
  const [data, setData] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const { get, loaded } = useContent('homepage');

  useEffect(() => {
    API.get('/homepage').then(r => setData(r.data)).catch(() => {});
    API.get('/testimonials').then(r => {
      const t = r.data?.testimonials;
      if (t?.length > 0) setTestimonials(t);
      else setTestimonials([
        { quote: "Plush Residence made our Toronto getaway absolutely perfect.", author: "Sarah M.", role: "Guest from Toronto", rating: 5 },
        { quote: "Exceptional service from start to finish. The property exceeded all expectations.", author: "Michael C.", role: "Guest from Ottawa", rating: 5 },
        { quote: "Our family vacation was unforgettable. Spacious, clean, beautifully designed.", author: "Isabella R.", role: "Guest from Niagara Falls", rating: 5 },
      ]);
    }).catch(() => {});
  }, []);

  const heroImages = (() => {
    const defaults = [
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=600&auto=format',
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=600&auto=format',
      'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=600&auto=format',
      'https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?w=600&auto=format',
    ];
    if (data?.featured_locations?.length > 0) {
      const imgs = data.featured_locations.map(l => getImageUrl(l.hero_image || l.thumbnail_image)).filter(Boolean);
      return imgs.length >= 4 ? imgs.slice(0, 4) : defaults;
    }
    return defaults;
  })();

  const steps = [
    { num: '01', title: get('approach_step1_title'), text: get('approach_step1_text') },
    { num: '02', title: get('approach_step2_title'), text: get('approach_step2_text') },
    { num: '03', title: get('approach_step3_title'), text: get('approach_step3_text') },
    { num: '04', title: get('approach_step4_title'), text: get('approach_step4_text') },
  ];

  return (
    <div data-testid="home-page" className="bg-[#101010]">
      <SEO
        path="/"
        description="Discover premium luxury short-term rentals across Ontario. World-class accommodations in Toronto, Mississauga, Niagara Falls & Ottawa. Book your dream stay at Plush Residence."
        keywords="luxury rentals Ontario, premium short-term rentals Toronto, luxury accommodations Mississauga, Niagara Falls vacation rentals, Ottawa luxury stays, PlushResidence.ca"
        jsonLd={[businessJsonLd, websiteJsonLd]}
      />

      {/* ===== HERO ===== */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden">
        <div className="absolute inset-0 grid-pattern" />
        <div className="relative z-10 pt-32 pb-8">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
              className="text-gold text-[11px] uppercase tracking-[0.4em] font-bold mb-8"><Editable page="homepage" field="hero_label">{get('hero_label')}</Editable></motion.p>
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.8 }}>
              <Editable page="homepage" field="hero_title" as="h1" className="font-heading text-5xl sm:text-6xl lg:text-8xl text-white font-bold leading-[1.05] mb-8">{get('hero_title')}</Editable>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }}>
              <Editable page="homepage" field="hero_description" as="p" className="text-white/50 text-base sm:text-lg max-w-xl mx-auto mb-10 leading-relaxed">{get('hero_description')}</Editable>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7, duration: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/locations">
                <Button className="bg-gold hover:bg-gold-dark text-white h-13 px-10 text-xs uppercase tracking-[0.15em] font-semibold rounded-full transition-all duration-300 shadow-lg shadow-gold/20" data-testid="hero-explore-btn">
                  <Editable page="homepage" field="hero_btn_primary">{get('hero_btn_primary')}</Editable> <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/[0.12] text-white hover:bg-white/[0.04] h-13 px-10 text-xs uppercase tracking-[0.15em] font-semibold rounded-full" data-testid="hero-contact-btn">
                  <Editable page="homepage" field="hero_btn_secondary">{get('hero_btn_secondary')}</Editable>
                </Button>
              </Link>
            </motion.div>
          </div>

          <div className="w-full overflow-hidden mt-20">
            <div className="flex gap-3 sm:gap-4 justify-center px-4">
              {heroImages.map((img, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 60 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 + i * 0.1, duration: 0.7 }}
                  className={`relative flex-shrink-0 w-32 sm:w-44 lg:w-52 rounded-lg overflow-hidden ${i % 2 === 1 ? 'mt-6 sm:mt-10' : ''}`}>
                  <div className="aspect-[3/4]">
                    <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#101010]/60 to-transparent" />
                  </div>
                  <div className="absolute inset-[3px] border border-white/[0.07] rounded-lg pointer-events-none" />
                </motion.div>
              ))}
            </div>
            <div className="flex gap-8 justify-center mt-6">
              {heroImages.map((_, i) => <span key={i} className="text-[10px] text-white/25 font-medium tabular-nums">{i + 1}</span>)}
            </div>
          </div>
        </div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2 }} className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div animate={{ y: [0, 8, 0] }} transition={{ repeat: Infinity, duration: 2 }}>
            <div className="w-5 h-9 border border-white/[0.12] rounded-full flex justify-center pt-2"><div className="w-1 h-2 bg-gold rounded-full" /></div>
          </motion.div>
        </motion.div>
      </section>

      {/* ===== MARQUEE ===== */}
      <section className="border-y border-white/[0.05] py-5 overflow-hidden">
        <div className="marquee-track flex items-center gap-16 whitespace-nowrap">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="flex items-center gap-6">
              <span className="font-heading text-lg sm:text-xl text-white/25 tracking-wide"><Editable page="homepage" field="marquee_text1">{get('marquee_text1')}</Editable></span>
              <span className="w-1.5 h-1.5 rounded-full bg-gold/30" />
              <span className="font-heading text-lg sm:text-xl text-white/25 tracking-wide italic"><Editable page="homepage" field="marquee_text2">{get('marquee_text2')}</Editable></span>
              <span className="w-1.5 h-1.5 rounded-full bg-gold/30" />
            </span>
          ))}
        </div>
      </section>

      {/* ===== WHO WE ARE ===== */}
      <section className="py-28 lg:py-40 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            <motion.div initial={{ opacity: 0, x: -50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }} className="relative">
              <div className="relative rounded-xl overflow-hidden aspect-[4/5]">
                <EditableImage page="homepage" field="who_image" src={get('who_image')} alt="Luxury interior" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#101010]/40 to-transparent pointer-events-none" />
                <CornerOrnament size={28} offset="12px" />
              </div>
              <div className="absolute -bottom-6 -right-4 sm:-right-6 bg-gold text-white px-5 py-4 rounded-xl shadow-2xl shadow-gold/20">
                <p className="text-2xl font-bold">5<span className="text-sm">.0</span></p>
                <div className="flex gap-0.5 my-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3 h-3 fill-white text-white" />)}</div>
                <p className="text-[9px] uppercase tracking-widest opacity-80">Guest Rating</p>
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 50 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}>
              <Editable page="homepage" field="who_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-5">{get('who_label')}</Editable>
              <Editable page="homepage" field="who_title" as="h2" className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white font-bold leading-[1.1] mb-6">{get('who_title')}</Editable>
              <Editable page="homepage" field="who_description" as="p" className="text-white/50 text-base leading-relaxed mb-10 max-w-md">{get('who_description')}</Editable>
              <Link to="/about">
                <Button variant="outline" className="rounded-full border-white/[0.12] text-white hover:bg-gold hover:border-gold h-12 px-8 text-xs uppercase tracking-[0.15em] font-semibold transition-all duration-300" data-testid="about-cta">
                  About Us <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <div className="grid grid-cols-3 gap-6 mt-14 pt-10 border-t border-white/[0.08]">
                <div>
                  <Editable page="homepage" field="who_stat1_val" as="p" className="text-3xl sm:text-4xl font-bold text-white font-heading">{get('who_stat1_val')}</Editable>
                  <Editable page="homepage" field="who_stat1_label" as="p" className="text-[10px] text-white/35 uppercase tracking-[0.2em] mt-1">{get('who_stat1_label')}</Editable>
                </div>
                <div>
                  <Editable page="homepage" field="who_stat2_val" as="p" className="text-3xl sm:text-4xl font-bold text-white font-heading">{get('who_stat2_val')}</Editable>
                  <Editable page="homepage" field="who_stat2_label" as="p" className="text-[10px] text-white/35 uppercase tracking-[0.2em] mt-1">{get('who_stat2_label')}</Editable>
                </div>
                <div>
                  <Editable page="homepage" field="who_stat3_val" as="p" className="text-3xl sm:text-4xl font-bold text-white font-heading">{get('who_stat3_val')}</Editable>
                  <Editable page="homepage" field="who_stat3_label" as="p" className="text-[10px] text-white/35 uppercase tracking-[0.2em] mt-1">{get('who_stat3_label')}</Editable>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== FEATURED PROPERTIES ===== */}
      {data?.featured_listings?.length > 0 && (
        <section className="py-28 lg:py-40 bg-[#161616]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-16">
              <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                <Editable page="homepage" field="featured_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-4">{get('featured_label')}</Editable>
                <Editable page="homepage" field="featured_title" as="h2" className="font-heading text-4xl sm:text-5xl text-white font-bold leading-tight">{get('featured_title')}</Editable>
              </motion.div>
              <Link to="/locations">
                <Button variant="outline" className="rounded-full border-white/[0.12] text-white hover:bg-gold hover:border-gold h-11 px-7 text-xs uppercase tracking-[0.15em] font-semibold transition-all duration-300" data-testid="view-all-properties">
                  View All <ArrowRight className="w-3.5 h-3.5 ml-2" />
                </Button>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.featured_listings.slice(0, 3).map((l, i) => <ListingCard key={l.id} listing={l} index={i} />)}
            </div>
          </div>
        </section>
      )}

      {/* ===== OUR APPROACH ===== */}
      <section className="py-28 lg:py-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <Editable page="homepage" field="approach_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-4">{get('approach_label')}</Editable>
            <Editable page="homepage" field="approach_title" as="h2" className="font-heading text-4xl sm:text-5xl text-white font-bold">{get('approach_title')}</Editable>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {steps.map((step, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.5 }}
                className="group relative bg-[#1c1c1c] rounded-xl p-8 border border-white/[0.08] hover:border-gold/30 transition-all duration-500" data-testid={`approach-step-${i + 1}`}>
                <CornerOrnament size={16} offset="8px" />
                <span className="text-5xl font-bold text-white/[0.03] font-heading block mb-4">{step.num}</span>
                <Editable page="homepage" field={`approach_step${i+1}_title`} as="h3" className="font-heading text-lg font-bold text-white mb-3 group-hover:text-gold transition-colors duration-300">{step.title}</Editable>
                <Editable page="homepage" field={`approach_step${i+1}_text`} as="p" className="text-sm text-white/45 leading-relaxed">{step.text}</Editable>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== LOCATIONS SHOWCASE ===== */}
      {data?.featured_locations?.length > 0 && (
        <section className="py-28 lg:py-40 bg-[#161616]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <Editable page="homepage" field="locations_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-4">{get('locations_label')}</Editable>
              <Editable page="homepage" field="locations_title" as="h2" className="font-heading text-4xl sm:text-5xl text-white font-bold">{get('locations_title')}</Editable>
            </motion.div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {data.featured_locations.map((loc, i) => (
                <motion.div key={loc.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1, duration: 0.6 }}>
                  <Link to={`/locations/${loc.slug}`} className="group relative block overflow-hidden rounded-xl aspect-[3/4]" data-testid={`loc-card-${loc.slug}`}>
                    <img src={getImageUrl(loc.hero_image || loc.thumbnail_image)} alt={loc.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                    <CornerOrnament size={24} offset="10px" color="rgba(255,255,255,0.12)" />
                    <div className="absolute bottom-0 left-0 right-0 p-6">
                      <h3 className="font-heading text-2xl text-white font-bold mb-1">{loc.name}</h3>
                      <p className="text-sm text-white/55 line-clamp-2 mb-4">{loc.short_description}</p>
                      <span className="inline-flex items-center gap-2 text-[10px] text-gold uppercase tracking-[0.2em] font-bold group-hover:gap-3 transition-all duration-300">
                        Explore <ArrowRight className="w-3 h-3" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-28 lg:py-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-20">
            <Editable page="homepage" field="testimonials_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-4">{get('testimonials_label')}</Editable>
            <Editable page="homepage" field="testimonials_title" as="h2" className="font-heading text-4xl sm:text-5xl text-white font-bold mb-6">{get('testimonials_title')}</Editable>
            <Editable page="homepage" field="testimonials_description" as="p" className="text-white/35 text-sm max-w-md mx-auto">{get('testimonials_description')}</Editable>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative bg-[#1c1c1c] rounded-xl p-8 border border-white/[0.08]" data-testid={`testimonial-${i}`}>
                <CornerOrnament size={16} offset="8px" />
                <div className="flex gap-0.5 mb-6">{[...Array(5)].map((_, j) => <Star key={j} className={`w-3.5 h-3.5 ${j < (t.rating || 5) ? 'fill-gold text-gold' : 'text-white/15'}`} />)}</div>
                <p className="text-white/55 text-sm leading-relaxed mb-8 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3 pt-4 border-t border-white/[0.08]">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center">
                    <span className="text-gold text-sm font-bold">{t.author[0]}</span>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white">{t.author}</p>
                    <p className="text-[11px] text-white/35">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="relative py-32 lg:py-44 overflow-hidden">
        <div className="absolute inset-0">
          <EditableImage page="homepage" field="cta_image" src={get('cta_image')} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/70 pointer-events-none" />
        </div>
        <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <Editable page="homepage" field="cta_label" as="p" className="text-gold text-[11px] uppercase tracking-[0.3em] font-bold mb-6">{get('cta_label')}</Editable>
            <Editable page="homepage" field="cta_title" as="h2" className="font-heading text-3xl sm:text-4xl lg:text-6xl text-white font-bold mb-6 leading-tight">{get('cta_title')}</Editable>
            <Editable page="homepage" field="cta_description" as="p" className="text-white/50 text-base mb-12 max-w-md mx-auto leading-relaxed">{get('cta_description')}</Editable>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/locations">
                <Button className="bg-gold hover:bg-gold-dark text-white h-14 px-10 text-xs uppercase tracking-[0.15em] font-semibold rounded-full shadow-lg shadow-gold/20 transition-all" data-testid="cta-browse">
                  <Editable page="homepage" field="cta_btn_primary">{get('cta_btn_primary')}</Editable> <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white/[0.12] text-white hover:bg-white/[0.04] h-14 px-10 text-xs uppercase tracking-[0.15em] font-semibold rounded-full">
                  <Editable page="homepage" field="cta_btn_secondary">{get('cta_btn_secondary')}</Editable>
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
